import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { $getRoot } from 'lexical'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $convertFromMarkdownString, $convertToMarkdownString, TRANSFORMERS } from '@lexical/markdown'
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'

/**
 * Syncs an external controlled value into the Lexical editor state.
 * Supports both markdown and HTML modes — compares the editor's current
 * output in the matching format and only writes when the value differs.
 */
const ControlledValuePlugin = ({ value, isMarkdown }) => {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    if (value === undefined || value === null) return
    editor.getEditorState().read(() => {
      const currentOutput = isMarkdown
        ? $convertToMarkdownString(TRANSFORMERS)
        : $generateHtmlFromNodes(editor)
      if (currentOutput === value) return

      editor.update(() => {
        const root = $getRoot()
        root.clear()
        if (typeof value === 'string' && value) {
          if (isMarkdown) {
            $convertFromMarkdownString(value, TRANSFORMERS)
          } else {
            const parser = new DOMParser()
            const dom = parser.parseFromString(value, 'text/html')
            const nodes = $generateNodesFromDOM(editor, dom)
            root.append(...nodes)
          }
        }
      }, { discrete: true })
    })
  }, [value, editor, isMarkdown])

  return null
}

ControlledValuePlugin.propTypes = {
  value: PropTypes.string,
  isMarkdown: PropTypes.bool
}

ControlledValuePlugin.defaultProps = {
  value: '',
  isMarkdown: false
}

export default ControlledValuePlugin
